import * as Yup from "yup";
import {
  isCompleteSortCode,
  SORT_CODE_INVALID_MESSAGE,
} from "components/Form/Input/sortCodeValidation";
import { inputToFloat } from "utils/calcs";
import { VALIDATION_REQUIRED } from "utils/validation";

import { PAID_TO_CREDITOR } from "./consts";
import type { DebtConsolidationFormValues } from "./types";
import { getCashOutPurposeRowKeys, getTotalCashOut } from "./utils";

const ACCOUNT_NUMBER_REQUIRED_LENGTH = 8;
const ACCOUNT_NUMBER_REQUIRED_MESSAGE = "Please provide a valid account number";
const PROVIDER_TYPE_CREDIT_CARD = "Credit Card";
const REFERENCE_CREDIT_CARD_TYPE_MIN_LENGTH = 15;
const REFERENCE_CREDIT_CARD_TYPE_MAX_LENGTH = 16;
const REFERENCE_CREDIT_CARD_TYPE_REQUIRED_MESSAGE =
  "Please provide a valid reference";
const REFERENCE_MAX_LENGTH = 18;
const REFERENCE_MAX_LENGTH_MESSAGE =
  "Reference can be no more than 18 characters";

const isParsableSettlementAmount = (v: unknown) => {
  if (v === "" || v == null) {
    return false;
  }
  const n = inputToFloat(v);
  return typeof n === "number" && !Number.isNaN(n);
};

const isNonEmptyTrimmedString = (value: unknown) =>
  value != null && String(value).trim() !== "";

const isValidAccountNumber = (value: unknown) =>
  value != null &&
  typeof value === "string" &&
  value.trim() !== "" &&
  value.trim().length === ACCOUNT_NUMBER_REQUIRED_LENGTH;

const isValidReferenceCC = (value: unknown) =>
  value != null &&
  typeof value === "string" &&
  value.trim() !== "" &&
  value.trim().length >= REFERENCE_CREDIT_CARD_TYPE_MIN_LENGTH &&
  value.trim().length <= REFERENCE_CREDIT_CARD_TYPE_MAX_LENGTH;

function requiredWhenTotalCashOutPositive(
  loanAmount: number,
  isPresent: (value: unknown) => boolean,
): (this: Yup.TestContext, value: unknown) => boolean {
  return function (value) {
    const parent = this.parent as DebtConsolidationFormValues;
    const total = getTotalCashOut(parent.creditProviders ?? [], loanAmount);
    if (total <= 0) {
      return true;
    }
    return isPresent(value);
  };
}

/** Same as {@link FormInputSortCode} `rules.validate` when a sort code is required. */
function validateRequiredCompleteSortCode(
  this: Yup.TestContext,
  value: unknown,
) {
  const s = String(value ?? "").trim();
  if (!s) {
    return this.createError({ message: VALIDATION_REQUIRED });
  }
  if (!isCompleteSortCode(s)) {
    return this.createError({ message: SORT_CODE_INVALID_MESSAGE });
  }
  return true;
}

function requiredCompleteSortCodeWhenTotalCashOutPositive(loanAmount: number) {
  return function (this: Yup.TestContext, value: unknown) {
    const parent = this.parent as DebtConsolidationFormValues;
    const total = getTotalCashOut(parent.creditProviders ?? [], loanAmount);
    if (total <= 0) {
      return true;
    }
    return validateRequiredCompleteSortCode.call(this, value);
  };
}

export function createDebtConsolidationValidationSchema(loanAmount: number) {
  const whenCashOutPositive = requiredWhenTotalCashOutPositive(
    loanAmount,
    isNonEmptyTrimmedString,
  );

  const whenCashOutPositiveAccount = requiredWhenTotalCashOutPositive(
    loanAmount,
    isValidAccountNumber,
  );

  const whenCashOutPositiveSortCode =
    requiredCompleteSortCodeWhenTotalCashOutPositive(loanAmount);

  return Yup.object({
    uuid: Yup.string(),
    cashOuts: Yup.lazy((_, options) => {
      const parent = options.parent as DebtConsolidationFormValues | undefined;
      const creditProviders = parent?.creditProviders ?? [];

      // Skip cash out validation if we have no creditors to pay
      if (creditProviders.length === 0) {
        return Yup.object();
      }

      const totalConsolidated = creditProviders.reduce(
        (s, c) => s + Number(c.settlementAmount || 0),
        0,
      );
      const keys = getCashOutPurposeRowKeys(
        creditProviders,
        loanAmount,
        totalConsolidated,
      );

      if (keys.length === 0) {
        return Yup.object();
      }

      return Yup.object().shape(
        Object.fromEntries(
          keys.map((key) => [
            key,
            Yup.string()
              .trim()
              .required(VALIDATION_REQUIRED)
              .min(5, "Please provide a valid purpose"),
          ]),
        ),
      );
    }),

    creditProviders: Yup.array().of(
      Yup.object({
        provider: Yup.string().required(VALIDATION_REQUIRED),
        settlementAmount: Yup.mixed<number | string>()
          .required(VALIDATION_REQUIRED)
          .test("settlement-amount", VALIDATION_REQUIRED, (value) =>
            isParsableSettlementAmount(value),
          ),
        accountNumber: Yup.string().when("paidTo", {
          is: PAID_TO_CREDITOR,
          then: (schema) =>
            schema
              .test(
                "account-number-format",
                ACCOUNT_NUMBER_REQUIRED_MESSAGE,
                isValidAccountNumber,
              )
              .required(VALIDATION_REQUIRED),
          otherwise: (schema) => schema.nullable().optional(),
        }),
        sortCode: Yup.string().when("paidTo", {
          is: PAID_TO_CREDITOR,
          then: (schema) =>
            schema
              .nullable()
              .test(
                "sort-code-format",
                VALIDATION_REQUIRED,
                validateRequiredCompleteSortCode,
              ),
          otherwise: (schema) => schema.nullable().optional(),
        }),
        reference: Yup.string()
          .max(REFERENCE_MAX_LENGTH, REFERENCE_MAX_LENGTH_MESSAGE)
          .when("paidTo", {
            is: PAID_TO_CREDITOR,
            then: (schema) =>
              schema.when("providerType", {
                is: PROVIDER_TYPE_CREDIT_CARD,
                then: (schema) =>
                  schema.test(
                    "reference-format-cc",
                    REFERENCE_CREDIT_CARD_TYPE_REQUIRED_MESSAGE,
                    isValidReferenceCC,
                  ),
                otherwise: (schema) => schema.required(VALIDATION_REQUIRED),
              }),
            otherwise: (schema) => schema.nullable().optional(),
          }),
        paidTo: Yup.string().required(VALIDATION_REQUIRED),
      }),
    ),
    cashOutApplicantUuid: Yup.string().test(
      "when-total-cash-out-applicant",
      VALIDATION_REQUIRED,
      whenCashOutPositive,
    ),
    cashOutAccountNumber: Yup.string()
      .nullable()
      .optional()
      .test(
        "when-total-cash-out-account",
        ACCOUNT_NUMBER_REQUIRED_MESSAGE,
        whenCashOutPositiveAccount,
      ),
    cashOutSortCode: Yup.string().test(
      "when-total-cash-out-sort-code",
      VALIDATION_REQUIRED,
      whenCashOutPositiveSortCode,
    ),
  });
}
